import { useState, useCallback, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { HiOutlineExclamationCircle, HiOutlineLightBulb } from 'react-icons/hi'

import Dialog from '@/components/ui/Dialog/Dialog'
import { FormItem, Form } from '@/components/ui/Form'
import Button from '@/components/ui/Button/Button'
import { Alert } from '@/components/ui/Alert'
import Checkbox from '@/components/ui/Checkbox/Checkbox'
import PasswordInput from '@/components/shared/PasswordInput'
import Steps from '@/components/ui/Steps'
import Spinner from '@/components/ui/Spinner'

import { useMasterPasswordStore } from '@/store/masterPasswordStore'
import {
    initSodium,
    deriveKey,
    computeVerifier,
    buildMetadataPayload,
    zeroize,
} from '@/services/CryptoService'
import { apiSetupMasterPassword } from '@/services/MasterPasswordService'
import { generateTtl, apiStoreTtl } from '@/services/TtlService'
import type { TtlKeypairPayload } from '@/services/TtlService'

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------

const buildSchema = (t: (key: string) => string) =>
    z
        .object({
            password: z
                .string()
                .min(1, t('masterPasswordSetup.validation.passwordRequired'))
                .min(8, t('masterPasswordSetup.validation.passwordMinLength')),
            confirmPassword: z
                .string()
                .min(1, t('masterPasswordSetup.validation.confirmRequired')),
            acknowledged: z.boolean(),
        })
        .refine((d) => d.password === d.confirmPassword, {
            message: t('masterPasswordSetup.validation.confirmMismatch'),
            path: ['confirmPassword'],
        })
        .refine((d) => d.acknowledged, {
            message: t('masterPasswordSetup.validation.acknowledgementRequired'),
            path: ['acknowledged'],
        })

type FormValues = {
    password: string
    confirmPassword: string
    acknowledged: boolean
}

// ---------------------------------------------------------------------------
// Step types
// ---------------------------------------------------------------------------

type SetupStepStatus = 'pending' | 'in-progress' | 'complete' | 'error'

type SetupStep = {
    key: 'generateKey' | 'generateTtl' | 'save'
    labelKey: string
    status: SetupStepStatus
}

/** Map our step status to the Steps component's StepStatus string literals. */
function toStepsStatus(s: SetupStepStatus) {
    switch (s) {
        case 'complete':    return 'complete'   as const
        case 'error':       return 'error'      as const
        case 'in-progress': return 'in-progress' as const
        default:            return 'pending'    as const
    }
}

/** Return the `current` index expected by the Steps component. */
function currentIndex(steps: SetupStep[]): number {
    const idx = steps.findIndex(
        (s) => s.status === 'in-progress' || s.status === 'error',
    )
    if (idx !== -1) return idx
    if (steps.every((s) => s.status === 'complete')) return steps.length
    return 0
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MasterPasswordSetupModal = () => {
    const { t } = useTranslation()

    const modalOpen    = useMasterPasswordStore((s) => s.modalOpen)
    const pendingAction = useMasterPasswordStore((s) => s.pendingAction)
    const closeModal   = useMasterPasswordStore((s) => s.closeModal)
    const setMasterKey = useMasterPasswordStore((s) => s.setMasterKey)
    const setConfigured = useMasterPasswordStore((s) => s.setConfigured)
    const hasTtl       = useMasterPasswordStore((s) => s.hasTtl)
    const setHasTtl    = useMasterPasswordStore((s) => s.setHasTtl)

    // 'form' = showing the input form; 'progress' = showing step progress
    const [view, setView] = useState<'form' | 'progress'>('form')
    const [steps, setSteps] = useState<SetupStep[]>([])
    const [stepError, setStepError] = useState<string | null>(null)

    // Keep the last-entered password so Retry can re-run without re-showing the form
    const savedPasswordRef = useRef<string | null>(null)

    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
        reset,
        watch,
    } = useForm<FormValues>({
        defaultValues: { password: '', confirmPassword: '', acknowledged: false },
        resolver: zodResolver(buildSchema(t)),
        mode: 'onChange',
    })

    const acknowledged = watch('acknowledged')

    // -----------------------------------------------------------------------
    // Step runner
    // -----------------------------------------------------------------------

    const buildInitialSteps = useCallback(
        (needsTtl: boolean): SetupStep[] => {
            const list: SetupStep[] = [
                { key: 'generateKey', labelKey: 'masterPasswordSetup.stepGenerateKey', status: 'pending' },
            ]
            if (needsTtl) {
                list.push({ key: 'generateTtl', labelKey: 'masterPasswordSetup.stepGenerateTtl', status: 'pending' })
            }
            list.push({ key: 'save', labelKey: 'masterPasswordSetup.stepSave', status: 'pending' })
            return list
        },
        [],
    )

    const updateStep = (
        setter: React.Dispatch<React.SetStateAction<SetupStep[]>>,
        key: SetupStep['key'],
        status: SetupStepStatus,
    ) => {
        setter((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)))
    }

    const runSetup = useCallback(
        async (password: string) => {
            const needsTtl = !hasTtl
            const initialSteps = buildInitialSteps(needsTtl)
            setSteps(initialSteps)
            setStepError(null)
            setView('progress')

            // Yield so the progress view renders before the blocking Argon2id call.
            await new Promise((resolve) => setTimeout(resolve, 0))

            let masterKeyRef: { key: Uint8Array } | null = null
            let ttlPayload: TtlKeypairPayload | null = null

            const set = setSteps

            // Helper: defer a blocking synchronous call so the spinner renders first.
            const defer = <T,>(fn: () => T): Promise<T> =>
                new Promise((resolve, reject) =>
                    setTimeout(() => { try { resolve(fn()) } catch (e) { reject(e) } }, 0),
                )

            // -- Step: Generate Master Key --
            updateStep(set, 'generateKey', 'in-progress')
            try {
                await initSodium()
                const derived = await defer(() => {
                    const d = deriveKey(password)
                    const verifier = computeVerifier(d.masterKey)
                    const metadata = buildMetadataPayload(d, verifier)
                    zeroize(verifier)
                    return { derived: d, metadata }
                })
                const metadata = derived.metadata
                masterKeyRef = { key: derived.derived.masterKey }

                // -- Step: Generate TTL --
                if (needsTtl) {
                    updateStep(set, 'generateKey', 'complete')
                    updateStep(set, 'generateTtl', 'in-progress')
                    try {
                        ttlPayload = await defer(() => generateTtl(masterKeyRef!.key))
                    } catch {
                        updateStep(set, 'generateTtl', 'error')
                        setStepError(t('masterPasswordSetup.ttlErrorGeneric'))
                        zeroize(masterKeyRef.key)
                        masterKeyRef = null
                        return
                    }
                }

                updateStep(set, needsTtl ? 'generateTtl' : 'generateKey', 'complete')

                // -- Step: Save --
                updateStep(set, 'save', 'in-progress')
                try {
                    await apiSetupMasterPassword(metadata)
                    if (ttlPayload) {
                        await apiStoreTtl(ttlPayload)
                    }
                } catch {
                    updateStep(set, 'save', 'error')
                    setStepError(t('masterPasswordSetup.errorGeneric'))
                    zeroize(masterKeyRef.key)
                    masterKeyRef = null
                    return
                }

                updateStep(set, 'save', 'complete')

                // Success — transfer key ownership to the store
                setMasterKey(masterKeyRef.key)
                masterKeyRef = null
                setConfigured(true)
                if (needsTtl) setHasTtl(true)

                // Small delay so the user sees all steps green before close
                await new Promise((resolve) => setTimeout(resolve, 600))

                reset()
                savedPasswordRef.current = null
                setView('form')
                closeModal()
                pendingAction?.()
            } catch {
                updateStep(set, 'generateKey', 'error')
                setStepError(t('masterPasswordSetup.errorGeneric'))
                masterKeyRef?.key && zeroize(masterKeyRef.key)
            }
        },
        [hasTtl, buildInitialSteps, t, setMasterKey, setConfigured, setHasTtl, reset, closeModal, pendingAction],
    )

    // -----------------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------------

    const handleClose = () => {
        if (view === 'progress') return   // block closing while running
        reset()
        setStepError(null)
        savedPasswordRef.current = null
        setView('form')
        closeModal()
    }

    const onSubmit = async (values: FormValues) => {
        savedPasswordRef.current = values.password
        await runSetup(values.password)
    }

    const handleRetry = () => {
        if (savedPasswordRef.current) {
            void runSetup(savedPasswordRef.current)
        }
    }

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    const renderProgress = () => (
        <div className="py-2">
            <Steps vertical current={currentIndex(steps)}>
                {steps.map((step) => (
                    <Steps.Item
                        key={step.key}
                        title={t(step.labelKey)}
                        status={toStepsStatus(step.status)}
                        customIcon={
                            step.status === 'in-progress'
                                ? <Spinner size={16} />
                                : undefined
                        }
                    />
                ))}
            </Steps>

            {stepError && (
                <div className="mt-4 space-y-3">
                    <Alert type="danger" showIcon>
                        {stepError}
                    </Alert>
                    <div className="flex justify-end">
                        <Button variant="solid" onClick={handleRetry}>
                            {t('masterPasswordSetup.retryButton')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )

    const renderForm = () => (
        <>
            {/* Warning + Recommendation */}
            <div className="flex flex-col gap-2 mb-3">
                <div
                    className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-3"
                    role="note"
                    aria-label={t('masterPasswordSetup.warningTitle')}
                >
                    <div className="flex items-start gap-2">
                        <HiOutlineExclamationCircle className="text-red-500 mt-0.5 shrink-0 text-base" />
                        <div>
                            <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">
                                {t('masterPasswordSetup.warningTitle')}
                            </p>
                            <ul className="text-xs text-red-700 dark:text-red-400 space-y-0.5 list-disc list-inside">
                                <li>{t('masterPasswordSetup.warningLine1')}</li>
                                <li>{t('masterPasswordSetup.warningLine2')}</li>
                                <li>{t('masterPasswordSetup.warningLine3')}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-3">
                    <HiOutlineLightBulb className="shrink-0 text-base mt-0.5" />
                    <span>{t('masterPasswordSetup.recommendation')}</span>
                </div>
            </div>

            {/* Form */}
            <Form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <FormItem
                            label={t('masterPasswordSetup.passwordLabel')}
                            htmlFor="mps-password"
                            invalid={Boolean(errors.password)}
                            className="!mb-1"
                        >
                            <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                    <PasswordInput
                                        id="mps-password"
                                        placeholder={t('masterPasswordSetup.passwordPlaceholder')}
                                        autoComplete="new-password"
                                        {...field}
                                    />
                                )}
                            />
                        </FormItem>
                        {errors.password && (
                            <p className="mt-0.5 text-[11px] text-red-500 leading-tight">
                                {errors.password.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <FormItem
                            label={t('masterPasswordSetup.confirmPasswordLabel')}
                            htmlFor="mps-confirm-password"
                            invalid={Boolean(errors.confirmPassword)}
                            className="!mb-1"
                        >
                            <Controller
                                name="confirmPassword"
                                control={control}
                                render={({ field }) => (
                                    <PasswordInput
                                        id="mps-confirm-password"
                                        placeholder={t('masterPasswordSetup.confirmPasswordPlaceholder')}
                                        autoComplete="new-password"
                                        {...field}
                                    />
                                )}
                            />
                        </FormItem>
                        {errors.confirmPassword && (
                            <p className="mt-0.5 text-[11px] text-red-500 leading-tight">
                                {errors.confirmPassword.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Acknowledgement */}
                <div className="mt-3">
                    <Controller
                        name="acknowledged"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={(checked) => field.onChange(checked)}
                            >
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {t('masterPasswordSetup.acknowledgementLabel')}
                                </span>
                            </Checkbox>
                        )}
                    />
                    {errors.acknowledged && (
                        <p className="mt-1 text-xs text-red-500">
                            {errors.acknowledged.message}
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-1">
                    <Button
                        type="button"
                        variant="plain"
                        onClick={handleClose}
                    >
                        {t('masterPasswordSetup.cancelButton')}
                    </Button>
                    <Button
                        type="submit"
                        variant="solid"
                        disabled={!isValid || !acknowledged}
                    >
                        {t('masterPasswordSetup.submitButton')}
                    </Button>
                </div>
            </Form>
        </>
    )

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Dialog
            isOpen={modalOpen}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={view === 'form'}
            onClose={handleClose}
            closable={view === 'form'}
            width={540}
            aria={{
                labelledby: 'mps-title',
                describedby: 'mps-description',
            }}
        >
            <div className="px-1">
                {/* Header */}
                <h2
                    id="mps-title"
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-0.5"
                >
                    {t('masterPasswordSetup.title')}
                </h2>
                <p
                    id="mps-description"
                    className="text-xs text-gray-500 dark:text-gray-400 mb-3"
                >
                    {t('masterPasswordSetup.subtitle')}
                </p>

                {view === 'progress' ? renderProgress() : renderForm()}
            </div>
        </Dialog>
    )
}

export default MasterPasswordSetupModal

